import { ListItemIcon, ListItemText, MenuItem, Typography } from "@mui/material";
import { Stack } from "@mui/system";
import { MessageSeverity, RatelistStatus } from "@sinch/core/entities/serverEnums";
import { MdsArchive } from "@sinch/icons";
import { parseMutationErrorsFromData } from "@sinch/utils/apollo";
import { useSnackbar } from "notistack";
import { propEq } from "ramda";
import { isNotNilOrEmpty } from "ramda-adjunct";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { MdsIcon } from "@sinch/components";
import { useRatelist } from "@sinch/components/application/Rate/useRatelist";
import { ConfirmDialogWrapper } from "@sinch/components/ConfirmDialogWrapper/ConfirmDialogWrapper";

export const ArchiveMenuItem = ({ id, onPerformedAction }: { id: string; onPerformedAction: () => void }) => {
  const { t } = useTranslation(["common", "draft"]);
  const { setStatus } = useRatelist();

  const { enqueueSnackbar } = useSnackbar();

  const handleConfirmArchive = useCallback(async () => {
    try {
      const response = await setStatus({ variables: { ratelistId: id, status: RatelistStatus.Archived } });
      if (response.data?.changeRatelistStatus.result) {
        enqueueSnackbar(t("archivedSuccessfully"), { variant: "success" });
        onPerformedAction();
      } else {
        const firstMessage = parseMutationErrorsFromData(response).find((message) =>
          propEq("severity", MessageSeverity.Error)(message)
        )?.message;
        if (isNotNilOrEmpty(firstMessage)) {
          enqueueSnackbar(firstMessage, { variant: "error" });
        } else {
          enqueueSnackbar(t("ratelistError"), { variant: "error" });
        }
      }
    } catch {
      enqueueSnackbar(t("ratelistError"), { variant: "error" });
    }
  }, [setStatus, id, onPerformedAction, enqueueSnackbar, t]);

  return (
    <ConfirmDialogWrapper
      content={
        <Stack spacing={1}>
          <Typography>{t("RateBuilder.confirmArchive")}</Typography>
          <Typography>{t("RateBuilder.whenThisItemIsArchived")}</Typography>
          <ul>
            <li>{t("RateBuilder.itWillNoLongerAppearInSelectionLists")}</li>
            <li>{t("RateBuilder.itWillBeRemoved")}</li>
          </ul>
        </Stack>
      }
      label={t("RateBuilder.confirmArchiveTitle")}
      onConfirm={handleConfirmArchive}
      render={({ openConfirmation }) => (
        <MenuItem color="warning" onClick={openConfirmation}>
          <ListItemIcon>
            <MdsIcon color="warning" icon={MdsArchive} />
          </ListItemIcon>
          <ListItemText sx={{ color: "warning.main" }}>{t("archive")}</ListItemText>
        </MenuItem>
      )}
    />
  );
};
